// MissionReady Radio — live push-to-talk controller.
//
// Owns the Radio state machine, the WebRTC session, the Deepgram streaming
// transcription and the single final-transcript persistence call. Deliberately
// independent of the existing Voice/DAR button and "Hey MR": the DAR recorder
// and its Deepgram flow are never touched here. Step 3 wires the Voice button
// to startRadioTransmission / stopRadioTransmission.
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { RADIO_DEFAULT_CHANNEL } from "@/lib/radio";
import { startRadioSession, saveRadioTransmission, claimRadioFloor, renewRadioFloor, releaseRadioFloor } from "@/lib/radio-live.functions";
import { RadioSession } from "@/lib/radio/radio-webrtc";
import { LiveKitRadioSession, type RadioConnectionState } from "@/lib/radio/radio-livekit";

type RadioTransport = {
  connect(): Promise<void>;
  startTransmission(): Promise<MediaStream>;
  stopTransmission(): Promise<void>;
  destroy(): Promise<void>;
  enableAudio(): Promise<void>;
};
import { RadioTranscriber } from "@/lib/radio/radio-deepgram";
import { isVoiceFocusBusy, onVoiceFocusChange } from "@/lib/radio/radio-mic-bus";
import {
  assembleFinalTranscript,
  canTransmit,
  initialRadioState,
  liveTranscript,
  radioReducer,
  type RadioErrorKind,
} from "@/lib/radio/radio-state";

function classifyMicError(e: unknown): RadioErrorKind {
  const name = (e as any)?.name ?? "";
  const msg = String((e as any)?.message ?? e ?? "");
  if (name === "NotAllowedError" || /denied|permission/i.test(msg)) return "mic-denied";
  if (name === "NotFoundError" || /mic-unavailable|not found/i.test(msg)) return "mic-unavailable";
  return "unknown";
}

export function useRadioController(args: {
  channelId: string | null;
  siteId: string | null;
  listenOnly?: boolean;
}) {
  const { user, currentOrg } = useAuth() as any;
  const [state, dispatch] = useReducer(radioReducer, initialRadioState);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  const [retry, setRetry] = useState(0);
  const generation = useRef(0);
  const startTask = useRef<Promise<void> | null>(null);
  const stopTask = useRef<Promise<void> | null>(null);
  const floor = useRef<{ channel_id: string; token: string } | null>(null);
  const heartbeat = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopRef = useRef<() => Promise<void>>(async () => {});
  const stateRef = useRef(state);
  stateRef.current = state;
  const [remoteTransmitter, setRemoteTransmitter] = useState<string | null>(null);

  const sessionRef = useRef<RadioTransport | null>(null);
  const teardownTask = useRef<Promise<void>>(Promise.resolve());
  const [connectionState, setConnectionState] = useState<RadioConnectionState>("disconnected");
  const transcriberRef = useRef<RadioTranscriber | null>(null);
  const grantRef = useRef<{ token: string | null } | null>(null);
  const startedAtRef = useRef<string | null>(null);
  const finalsRef = useRef<string[]>([]);
  const lastInterimRef = useRef("");

  const displayName =
    user?.user_metadata?.full_name || user?.email || "Officer";
  const orgId = currentOrg?.id ?? null;
  const userId = user?.id ?? null;

  const teardown = useCallback(async () => {
    await stopRef.current();
    try {
      await transcriberRef.current?.stop(0);
    } catch {
      /* ignore */
    }
    transcriberRef.current = null;
    try {
      await sessionRef.current?.destroy();
    } catch {
      /* ignore */
    }
    sessionRef.current = null;
    grantRef.current = null;
    startedAtRef.current = null;
    finalsRef.current = [];
    setRemoteTransmitter(null);
    setPlaybackBlocked(false);
    dispatch({ type: "disconnected" });
  }, []);

  // Connect / reconnect whenever the Radio context (site or thread) changes.
  useEffect(() => {
    let cancelled = false;
    if (!args.channelId || !args.siteId || !orgId || !userId) {
      teardownTask.current = teardown();
      return;
    }
    (async () => {
      await teardownTask.current;
      if (cancelled) return;
      const siteId = args.siteId;
      if (!siteId) return;
      dispatch({ type: "connect", siteId });
      try {
        // Server validates the caller's site access and mints the Deepgram grant.
        const grant = await startRadioSession({
          data: { organization_id: orgId, site_id: siteId },
        });
        if (cancelled) return;
        grantRef.current = { token: grant.deepgram?.token ?? null };
        if (!grant.deepgram) dispatch({ type: "transcription-unavailable" });

        const callbacks = {
          onParticipants: (list: any) => dispatch({ type: "presence", participants: list }),
          onRemoteTransmission: (uid: string, active: boolean) =>
            setRemoteTransmitter((cur) => (active ? uid : cur === uid ? null : cur)),
          onConnectionError: (message: string) => {
            void stopRef.current();
            dispatch({ type: "error", kind: "connection", message });
          },
          onConnectionState: (s: RadioConnectionState) => {
            setConnectionState(s);
            if (s === "disconnected" || s === "reconnecting") void stopRef.current();
          },
          onPlaybackBlocked: setPlaybackBlocked,
        };
        // USE_LIVEKIT: the server picks the transport per site.
        const session: RadioTransport =
          grant.transport === "livekit" && grant.livekit
            ? new LiveKitRadioSession(grant.livekit.url, grant.livekit.token, grant.user_id, callbacks)
            : new RadioSession(grant.channel_id, grant.user_id, displayName, callbacks);
        sessionRef.current = session;
        await session.connect();
        if (cancelled) {
          await session.destroy();
          return;
        }
        sessionRef.current = session;
        dispatch({ type: "connected", channelId: grant.channel_id });
      } catch (e) {
        if (cancelled) return;
        await sessionRef.current?.destroy().catch(() => {});
        sessionRef.current = null;
        const msg = String((e as any)?.message ?? e);
        dispatch({
          type: "error",
          kind: /authoriz|permission|not allowed/i.test(msg) ? "authorization" : "connection",
          message: /authoriz|permission/i.test(msg)
            ? "You're not authorized for this site's Radio"
            : "Radio connection unavailable",
        });
      }
    })();
    return () => {
      cancelled = true;
      teardownTask.current = teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [args.channelId, args.siteId, orgId, userId, retry]);

  // Transmission timer.
  useEffect(() => {
    if (state.phase !== "transmitting" || !state.transmissionStartedAt) {
      setElapsedMs(0);
      return;
    }
    const base = new Date(state.transmissionStartedAt).getTime();
    setElapsedMs(Date.now() - base);
    const t = setInterval(() => setElapsedMs(Date.now() - base), 500);
    return () => clearInterval(t);
  }, [state.phase, state.transmissionStartedAt]);

  const startRadioTransmission = useCallback((): Promise<void> => {
    const session = sessionRef.current;
    const current = stateRef.current;
    if (!session || startTask.current || stopTask.current || startedAtRef.current || !canTransmit(current) || !current.channelId || isVoiceFocusBusy()) return Promise.resolve();
    const id = ++generation.current;
    const claim = { channel_id: current.channelId, token: crypto.randomUUID() };
    lastInterimRef.current = "";
    finalsRef.current = [];
    const task = (async () => {
      try {
        const claimed = await claimRadioFloor({ data: claim });
        if (!claimed) {
          toast.info("Radio channel busy — wait for the current speaker");
          return;
        }
        floor.current = claim;
        if (id !== generation.current) return;
        const stream = await session.startTransmission();
        if (id !== generation.current || isVoiceFocusBusy()) {
          await session.stopTransmission();
          return;
        }
        const startedAt = new Date().toISOString();
        startedAtRef.current = startedAt;
        dispatch({ type: "transmit-start", startedAt });
        heartbeat.current = setInterval(() => {
          void renewRadioFloor({ data: claim }).then((renewed) => {
            if (!renewed) void stopRef.current();
          }).catch(() => {
            toast.error("Radio connection lost — transmission stopped");
            void stopRef.current();
          });
        }, 4000);
        const transcriber = new RadioTranscriber({
          onInterim: (text) => {
            if (transcriberRef.current !== transcriber) return;
            lastInterimRef.current = text;
            dispatch({ type: "interim", text });
          },
          onFinal: (text) => {
            if (transcriberRef.current !== transcriber) return;
            finalsRef.current.push(text);
            lastInterimRef.current = "";
            dispatch({ type: "final", text });
          },
          onUnavailable: () => dispatch({ type: "transcription-unavailable" }),
        });
        transcriberRef.current = transcriber;
        void (async () => {
          let token = grantRef.current?.token ?? null;
          try {
            if (orgId && args.siteId) {
              const g = await startRadioSession({ data: { organization_id: orgId, site_id: args.siteId } });
              token = g.deepgram?.token ?? null;
            }
          } catch { /* audio continues if transcription is unavailable */ }
          if (id === generation.current && transcriberRef.current === transcriber) await transcriber.start(stream, token);
        })();
      } catch (e) {
        if (id === generation.current) {
          const kind = classifyMicError(e);
          dispatch({ type: "error", kind, message: kind === "mic-denied" ? "Microphone permission required for Radio" : "Radio transmission couldn't start" });
        }
        await session.stopTransmission().catch(() => {});
      } finally {
        if (!startedAtRef.current && floor.current?.token === claim.token) {
          floor.current = null;
          await releaseRadioFloor({ data: claim }).catch(() => {});
        }
      }
    })();
    startTask.current = task;
    void task.finally(() => { if (startTask.current === task) startTask.current = null; });
    return task;
  }, [orgId, args.siteId]);

  const stopRadioTransmission = useCallback((): Promise<void> => {
    ++generation.current; // A release invalidates even an in-flight mic request.
    if (stopTask.current) return stopTask.current;
    const task = (async () => {
      await startTask.current;
      if (heartbeat.current) clearInterval(heartbeat.current);
      heartbeat.current = null;
      const endedAt = new Date().toISOString();
      const startedAt = startedAtRef.current;
      if (startedAt) dispatch({ type: "transmit-stopping" });
      // Mute first, not after Deepgram's final-result wait.
      await sessionRef.current?.stopTransmission().catch(() => {});
      const heldFloor = floor.current;
      floor.current = null;
      if (heldFloor) await releaseRadioFloor({ data: heldFloor }).catch(() => {});
      await transcriberRef.current?.stop().catch(() => {});
      transcriberRef.current = null;
      const transcript = assembleFinalTranscript(finalsRef.current) || lastInterimRef.current.trim();
      finalsRef.current = [];
      lastInterimRef.current = "";
      startedAtRef.current = null;
      const channelId = stateRef.current.channelId;
      if (startedAt && transcript && channelId) {
        try {
          await saveRadioTransmission({ data: { channel_id: channelId, transcript, started_at: startedAt, ended_at: endedAt, radio_channel: RADIO_DEFAULT_CHANNEL } });
        } catch { toast.error("Radio transcript couldn't be saved"); }
      }
      if (startedAt) dispatch({ type: "transmit-done" });
    })();
    stopTask.current = task;
    void task.finally(() => { if (stopTask.current === task) stopTask.current = null; });
    return task;
  }, []);
  stopRef.current = stopRadioTransmission;

  useEffect(() => onVoiceFocusChange((busy) => {
    if (busy) void stopRef.current();
  }), []);

  // Mobile/browser interruption: never leave a hot mic behind.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onHidden = () => {
      if (document.visibilityState === "hidden") {
        void stopRadioTransmission();
      }
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => document.removeEventListener("visibilitychange", onHidden);
  }, [state.phase, stopRadioTransmission]);

  const live = useMemo(() => liveTranscript(state), [state]);

  return {
    state,
    live,
    elapsedMs,
    remoteTransmitter,
    connectionState,
    playbackBlocked,
    enableAudio: async () => { await sessionRef.current?.enableAudio(); },
    reconnect: () => setRetry((value) => value + 1),
    canTransmit: canTransmit(state) && connectionState !== "reconnecting" && !state.participants.some((p) => p.userId !== userId && p.state === "transmitting"),
    startRadioTransmission,
    stopRadioTransmission,
    clearError: () => dispatch({ type: "clear-error" }),
  };
}
